<template>
    <div class="flex flex-wrap box justify-between gap-y-3 gap-x-8 xl:gap-x-12">
        <div class="w-1/3">
            <div class="flex mb-2">
                <jet-label for="type" value="Load Type" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                    <multiselect
                        class="multi-lg h-7 text-base"
                        v-model="form.type"
                        :options="loadTypeOptions"
                        :canClear="false"
                        :canDeselect="false"
                        :disabled="form.id !== null && form.type == 'pickup'"
                    />
                    <jet-input-error :message="$page.props.errors.type" class="m1" />
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="date" value="Date:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <datepicker inputClassName="dp-custom-input"
                        v-model="form.date"
                        id="date"
                        weekStart="0"
                        monthNameFormat="long"
                        :enableTimePicker="false"
                        :autoApply="true"
                        :is24="false"
                        :clearable="false"
                        :format="formatDate"
                        position="right"
                        :disabled="form.id !== null && form.type == 'pickup'"
                    />
                    <jet-input-error :message="$page.props.errors.date" class="m-1" />
                </div>
            </div>

            <div v-if="form.type == 'delivery'" class="flex mb-2">
                <jet-label for="route_type" value="Route Type" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                    <multiselect
                        class="multi-lg h-7 text-base"
                        v-model="form.route_type"
                        :options="routeTypeOptions"
                        :canClear="false"
                        :canDeselect="false"
                    />
                    <jet-input-error :message="$page.props.errors.route_type" class="m1" />
                </div>
            </div>
            
            <div v-if="form.type == 'delivery'" class="flex mb-2">
                <jet-label for="route" value="Route:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <jet-input type="text" class="block w-full" v-model="form.route" id="route" />
                    <jet-input-error :message="$page.props.errors.route" class="m-1" />
                </div>
            </div>
        </div>

        <div class="w-1/3">
            <div v-if="form.type == 'delivery'" class="flex mb-2">
                <jet-label for="truck" value="Truck:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <jet-input type="text" class="block w-full" v-model="form.truck" id="truck" />
                    <jet-input-error :message="$page.props.errors.truck" class="m-1" />
                </div>
            </div>

            <div v-if="form.type == 'delivery'" class="flex mb-2">
                <jet-label for="driver" value="Driver:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <jet-input type="text" v-model="form.driver" class="w-full" id="driver" />
                    <jet-input-error :message="$page.props.errors.driver" class="m-1" />
                </div>
            </div>

            <div v-if="form.type == 'delivery'" class="flex mb-2">
                <jet-label for="departure_time" value="Departure:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <jet-input type="time" v-model="form.departure_time" class="w-full" id="departure_time" />
                    <jet-input-error :message="$page.props.errors.departure_time" class="m-1" />
                </div>
            </div>

            <div v-if="form.type == 'pickup'" class="flex mb-2">
                <jet-label for="location_id" value="Location" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                    <multiselect
                        class="multi-lg h-7 text-base"
                        v-model="form.location_id"
                        :options="locationOptions"
                        valueProp="id"
                        label="label"
                        :canClear="false"
                        :canDeselect="false"
                        :disabled="form.id !== null"
                    />
                    <jet-input-error :message="$page.props.errors.location_id" class="m1" />
                </div>
            </div>

            <div v-if="form.type == 'pickup'" class="flex mb-2">
                <jet-label for="shipping_time" value="Pickup Time:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <multiselect
                        class="multi-lg h-7 text-base"
                        v-model="form.shipping_time"
                        :options="shippingTimeOptions"
                        :canClear="false"
                        :canDeselect="false"
                        :disabled="form.id !== null"
                    />
                    <jet-input-error :message="$page.props.errors.shipping_time" class="m-1" />
                </div>
            </div>
        </div>

        <div class="w-1/3">
            <div v-if="!is_edit" class="flex mb-2">
                <jet-label for="order_numbers" value="Orders:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <textarea class="w-full resize-none" rows="4" v-model="form.order_numbers" id="order_numbers" placeholder="e.g. #700000 1201-DEL100 #700001 1201-DEL101 1201-DEL102 #700002"></textarea>
                    <div class="text-xs text-gray-400">Download Routific's solution after the project has been optimized, and copy column X (Notes) into the provided text field for each load.</div>
                    <jet-input-error :message="$page.props.errors.order_numbers" class="m-1" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import { DateTime } from 'luxon';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    components: {
        Datepicker,
        JetInput,
        JetInputError,
        JetLabel,
        Multiselect,
    },

    props: [
        'form',
        'load',
        'is_edit',
        'locations',
    ],

    data() {
        return {
            routeTypeOptions: [
                { value: 'school', label: 'School' },
                { value: 'main', label: 'Main' },
            ],
            
            loadTypeOptions: [
                { value: 'pickup', label: 'Pickup' },
                { value: 'delivery', label: 'Delivery' },
            ],

            locationOptions: this.locations,
            daysOfWeek: '',
            hours: '',
        }
    },

    methods: {
        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },
    },

    computed: {
        pickup_location () {
            if (this.form.location_id === null) return null;
            return this.locations.find(location => location.id == this.form.location_id);
        },

        shippingTimeOptions () {
            let shippingTimeOptions = [];
            if (this.pickup_location !== null)
            {
                shippingTimeOptions = this.pickup_location.shipping_times;
            }
            return shippingTimeOptions;
        },
    },

    watch: {
        'form.location_id':{
            handler () {
                this.form.shipping_time = null;
            },
            immediate: false,
        }
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
.box>* {
    flex: 1 1 300px;
}
</style>

<style scoped>
input:active.disabled,
input:focus.disabled,
input:hover.disabled,
input.disabled {
    @apply bg-gray-300;
}
</style>
